import { updateEntities } from "redux-query";
import { ajax } from "lib/observable";
import "rxjs/add/operator/map";
import {
    WHITEBOARD_COMPONENT_MUTATE,
    WHITEBOARD_PRODUCT_IMAGE_CHANGE,
    WHITEBOARD_PRODUCT_IMAGE_CHANGE_COMPLETE,
    WHITEBOARD_PRODUCTS_CART,
    WHITEBOARD_PRODUCTS_FETCH,
    WHITEBOARD_PRODUCTS_FETCH_COMPLETED,
    WHITEBOARD_PRODUCTS_ORDER_FLOW_UPDATE,
    WHITEBOARD_PRODUCTS_ORDER_FLOW_UPDATE_COMPLETED,
} from "constants/action-types";
import { Items as ItemsSchema } from "schemas/items";
import siteConfig from "site-config";
import { normalize } from "normalizr";
import { Observable } from "rxjs";
import { add as snackbarAdd } from "actions/snackbars";
import { cartFetch, cartItemsFetch, cartItemAdd } from "actions/cart";
import { products } from "actions/whiteboard";
import { formatMessage } from "lib/globalize-util";
import { translateValues } from "containers/gallery";
import { APIError } from "lib/errors";

const recordCache = {};

const getModelForSchema = (schemaId, builder, schemas = {}) => {
    let Record = recordCache[schemaId];
    if (!Record) {
        // build Record
        const schema = schemas[schemaId];
        if (!schema) {
            // this should never happen
            throw new Error(`Schema ${schemaId} not found`);
        }

        Record = builder.create ? builder.create(schema) : builder(schema);
        recordCache[schemaId] = Record;
    }
    return Record;
};

const getRecordsFromSchema = (schema, records = {}) => {
    if (schema.record) {
        records[schema.key] = schema.record;
    }
    if (schema.schema) {
        if (schema.schema._key) {
            getRecordsFromSchema(schema.schema, records);
        } else {
            Object.keys(schema.schema).forEach((key) => {
                getRecordsFromSchema(schema.schema[key], records);
            });
        }
    }
    return records;
};

const setupFiltersOrNextPage = (payload, store) => {
    const { scope, entities, whiteboard } = store.getState();

    if ((payload as any).nextPage && whiteboard.productsNextPage) {
        return {
            nextPage: whiteboard.productsNextPage,
            query: { __search: whiteboard.searchTermForProducts },
            schemas: entities.schemas,
            scope,
        };
    }

    const filters: any = (payload as any).filters || {};
    let sortPreference = {};

    if (scope.brand && scope.brand.hasPreference("gallery_sort")) {
        sortPreference = translateValues(scope.brand.hasPreference("gallery_sort"));
    }

    const query = {
        __archived: false,
        __fields: "__all",
        __limit: 25,
        __meta: true,
        __populate: "__variant",
        __sort: (sortPreference as any).sort || "__created_on",
        __sort_order: (sortPreference as any).sortOrder || "desc",
        __view_disabled: false,
    };
    if (scope.viewAs) {
        (query as any).__company = scope.viewAs._id;
    }
    if (filters) {
        if (filters.__active) {
            switch (filters.__active) {
                case "active":
                    filters.__active = true;
                    break;
                case "inactive":
                    filters.__view_disabled = true;
                    filters.__active = false;
                    break;
                default:
                    filters.__view_disabled = true;
                    delete filters.__active;
            }
        }
    }

    return { query: { ...query, ...filters }, scope, schemas: entities.schemas, resolve: (payload as any).resolve };
};

const normalizeData = (response, schemas, scope) => {
    const records = getRecordsFromSchema(ItemsSchema);
    const data = normalize(response.data || response, ItemsSchema);

    Object.keys(records).forEach((key) => {
        const recordOrBuilder = records[key];
        let Record = recordOrBuilder;
        const collection = data.entities[key];
        if (collection) {
            const keys = Object.keys(collection);
            if (keys.length) {
                // FDM
                if (!recordOrBuilder.prototype.toJS) {
                    if (!scope.brand && !scope.brand._id) {
                        return;
                    }
                    // check if not Immutable Record
                    const schemaId = collection[keys[0]].__schema_id;
                    Record = getModelForSchema(schemaId, recordOrBuilder, schemas);
                }
                keys.forEach((id) => {
                    collection[id] = new Record(collection[id]);
                });
            }
        }
    });

    return data;
};

export const fetchProductsEpic = (action$, store) =>
    action$
        .ofType(WHITEBOARD_PRODUCTS_FETCH)
        .debounceTime(1500)
        .map(({ payload = {} }) => setupFiltersOrNextPage(payload, store))
        .switchMap(({ query, scope, schemas, nextPage, resolve }) => {
            const endpoint = `${siteConfig.api}/api/v3.0/query/items`;
            let body;
            if (nextPage) {
                body = nextPage;
            } else {
                body = { ...query };
                if (query.workingOrderItems) {
                    delete body.workingOrderItems;
                    body._id = query.workingOrderItems;
                }
            }
            return ajax({ url: endpoint, method: "POST", body })
                .flatMap(({ response, status, xhr }) => {
                    const data = normalizeData(response, schemas, scope);

                    if (resolve) {
                        process.nextTick(resolve); // resolve for react-virtualize
                    }

                    return [
                        updateEntities({
                            items: (currentItems) => {
                                return {
                                    ...currentItems,
                                    ...data.entities.items,
                                };
                            },
                        }),
                        {
                            payload: {
                                nextPage: response.nextPagePostBody,
                                results: data.result, // product-finder panel fetch
                                ...(query && query._id && { panelProductResults: data.result }), // product panel fetch
                                ...(query && query.__search && { searchTermForProducts: query.__search }),
                            },
                            type: WHITEBOARD_PRODUCTS_FETCH_COMPLETED,
                        },
                    ];
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was an error fetching your products"),
                            }),
                        ),
                        Observable.of({
                            error: true,
                            payload: error,
                            type: WHITEBOARD_PRODUCTS_FETCH_COMPLETED,
                        }),
                    ),
                );
        });

export const addProductsToCartEpic = (action$, store) =>
    action$
        .ofType(WHITEBOARD_PRODUCTS_CART)
        .map(({ payload }) => payload)
        .concatMap((payload) =>
            ajax({
                body: payload,
                method: "POST",
                url: `${siteConfig.api}/api/v2.0/orders/working/line-items/bulk`,
            })
                .mergeMap(({ response }) => {
                    if (!response) {
                        return Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was no response from the server"),
                            }),
                        );
                    }
                    if (response.length < 0) {
                        return Observable.of(
                            snackbarAdd({
                                message: formatMessage("Inactive items cannot be ordered"),
                            }),
                        );
                    }
                    return Observable.merge(
                        Observable.of((cartFetch as any)()),
                        Observable.of((cartItemsFetch as any)()),
                        Observable.of(snackbarAdd({ message: formatMessage("Items added") })),
                    );
                })
                .catch((error) =>
                    Observable.of(
                        snackbarAdd({
                            message: formatMessage("There was an error adding your products"),
                        }),
                    ),
                ),
        );

export const changeProductImage = (action$, store) => {
    let componentToUpdate;
    let productPosition;
    let whiteboardToUpdate;
    return action$
        .ofType(WHITEBOARD_PRODUCT_IMAGE_CHANGE)
        .debounceTime(300)
        .concatMap(({ payload }) => {
            const { url, component, position, whiteboard } = payload;
            whiteboardToUpdate = whiteboard;
            componentToUpdate = component;
            productPosition = position;
            return ajax({
                body: {
                    content_type: "image/jpeg",
                    external_url: `https:${url}`,
                },
                method: "POST",
                url: `${siteConfig.goAPI}/v1/uploads`,
            })
                .flatMap(({ response, status }) => {
                    if (response.data && response.data.length) {
                        return ajax({
                            body: {
                                board_id: whiteboardToUpdate.boardId,
                                kind: "IMAGE",
                                upload_id: response.data[0].id,
                            },
                            method: "POST",
                            url: `${siteConfig.goAPI}/v1/boards/${whiteboardToUpdate.boardId}/assets`,
                        }).catch((error) => Observable.of(error));
                    } else {
                        let errorMessage = formatMessage("Oops! Something went wrong. Please refresh and try again.");
                        if (response.status === 403) {
                            errorMessage = formatMessage("You do not have permission to edit this whiteboard");
                        }
                        throw new Error(errorMessage);
                    }
                })
                .catch((error) => Observable.of(error));
        })
        .flatMap(({ status, response, message }) => {
            if (status === 200 && response) {
                const data = response.data[0];
                const componentType = componentToUpdate.get("component");
                return [
                    {
                        payload: {
                            component: componentToUpdate,
                            propsOrKey: {
                                [componentType]: {
                                    [`product_${productPosition}`]: {
                                        product_image: {
                                            asset_id: data.asset_id,
                                            height: data.height,
                                            url: data.url,
                                            width: data.width,
                                            _isLoadingImage: false,
                                        },
                                    },
                                },
                            },
                            value: undefined,
                            whiteboard: whiteboardToUpdate,
                        },
                        type: WHITEBOARD_COMPONENT_MUTATE,
                    },
                    {
                        type: WHITEBOARD_PRODUCT_IMAGE_CHANGE_COMPLETE,
                    },
                ];
            } else if (!response && status >= 400) {
                return Observable.merge(
                    Observable.of({
                        type: WHITEBOARD_PRODUCT_IMAGE_CHANGE_COMPLETE,
                    }),
                    Observable.of(
                        snackbarAdd({
                            message: message
                                ? message
                                : formatMessage("There was an error uploading your product image"),
                        }),
                    ),
                );
            }
        })
        .catch((error) => {
            return Observable.merge(
                Observable.of({
                    type: WHITEBOARD_PRODUCT_IMAGE_CHANGE_COMPLETE,
                }),
            );
        });
};

export const updateOrderFlowTypeAndAddToCart = (action$, store) =>
    action$
        .ofType(WHITEBOARD_PRODUCTS_ORDER_FLOW_UPDATE)
        .map(({ payload }) => payload)
        .mergeMap(({ mode, adds }) => {
            const { cart } = store.getState();
            return ajax({
                body: JSON.stringify({ order_flow_type: mode }),
                method: "POST",
                url: `${siteConfig.api}/working/update/${cart.result}`,
            })
                .mergeMap((response) => {
                    const { ids } = adds;
                    const actions = [
                        {
                            payload: mode,
                            type: WHITEBOARD_PRODUCTS_ORDER_FLOW_UPDATE_COMPLETED,
                        },
                    ];
                    return ids
                        ? [...actions, products.addPageProductsToCart(ids)]
                        : [...actions, cartItemAdd(adds, {})];
                })
                .catch((error) => [
                    {
                        error: true,
                        payload: new APIError(
                            error.xhr.response.err.errors.order_flow_type.message,
                            error.xhr.status,
                            error.xhr.statusText,
                        ),
                        type: WHITEBOARD_PRODUCTS_ORDER_FLOW_UPDATE_COMPLETED,
                    },
                    snackbarAdd({ message: formatMessage("There was an error updating the order flow type") }),
                ]);
        });
